import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAdminAuth } from '../contexts/AdminAuthContext';
import {
  ShieldCheckIcon,
  BuildingOfficeIcon,
  UsersIcon,
  ChartBarIcon,
  CogIcon,
  ArrowRightOnRectangleIcon,
  Bars3Icon,
  XMarkIcon,
  ChevronRightIcon,
  ChevronDownIcon,
  ChatBubbleLeftRightIcon,
  CubeIcon,
  KeyIcon,
  UserGroupIcon,
  DocumentTextIcon
} from '@heroicons/react/24/outline';

const AdminLayout = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { admin, logout } = useAdminAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [organizationsExpanded, setOrganizationsExpanded] = useState(false);

  // Keep organizations menu expanded when on organization sub-pages
  useEffect(() => {
    const orgPaths = ['/admin/organizations', '/admin/organizations/users', '/admin/organizations/whatsapp-agents'];
    const isOnOrgPage = orgPaths.some(path => location.pathname.startsWith(path));
    setOrganizationsExpanded(isOnOrgPage);
  }, [location.pathname]);

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  const navigation = [
    { name: 'Dashboard', href: '/admin/dashboard', icon: ChartBarIcon },
    {
      name: 'Organizations',
      icon: BuildingOfficeIcon,
      hasSubmenu: true,
      submenu: [
        { name: 'All Organizations', href: '/admin/organizations', icon: BuildingOfficeIcon },
        { name: 'Users', href: '/admin/organizations/users', icon: UsersIcon },
        { name: 'WhatsApp Agents', href: '/admin/organizations/whatsapp-agents', icon: ChatBubbleLeftRightIcon },
      ]
    },
    { name: 'Roles & Permissions', href: '/admin/roles-permissions', icon: UserGroupIcon },
    { name: 'Package Management', href: '/admin/packages', icon: CubeIcon, superAdminOnly: true },
    { name: 'Admin Users', href: '/admin/admins', icon: UsersIcon },
    { name: 'System Settings', href: '/admin/settings', icon: CogIcon },
    { name: 'Audit Trails', href: '/admin/audit-trails', icon: DocumentTextIcon },
  ];

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="fixed inset-0 bg-gray-600 bg-opacity-75" onClick={() => setSidebarOpen(false)} />
        </div>
      )}

      {/* Sidebar */}
      <div className={`fixed inset-y-0 left-0 z-50 w-64 bg-gray-900 transform transition-transform duration-300 ease-in-out lg:translate-x-0 ${
        sidebarOpen ? 'translate-x-0' : '-translate-x-full'
      }`}>
        {/* Logo */}
        <div className="flex items-center justify-between h-16 px-4 bg-whatsapp-600">
          <div className="flex items-center">
            <ShieldCheckIcon className="h-8 w-8 text-white mr-2" />
            <span className="text-white text-lg font-bold">Admin Portal</span>
          </div>
          {/* Mobile close button */}
          <button
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden text-white hover:text-gray-300"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="mt-8 px-4">
          <ul className="space-y-2">
            {navigation.map((item) => {
              // Check super admin permission for restricted items
              if (item.superAdminOnly && admin?.role !== 'super_admin') {
                return null;
              }

              if (item.hasSubmenu) {
                const isOrganizationsActive = window.location.pathname.startsWith('/admin/organizations');
                return (
                  <li key={item.name}>
                    {/* Parent menu item */}
                    <button
                      onClick={() => setOrganizationsExpanded(!organizationsExpanded)}
                      className={`w-full flex items-center justify-between px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                        isOrganizationsActive
                          ? 'bg-whatsapp-600 text-white'
                          : 'text-gray-300 hover:bg-gray-800 hover:text-white'
                      }`}
                    >
                      <div className="flex items-center">
                        <item.icon className="h-5 w-5 mr-3" />
                        {item.name}
                      </div>
                      {organizationsExpanded ? (
                        <ChevronDownIcon className="h-4 w-4" />
                      ) : (
                        <ChevronRightIcon className="h-4 w-4" />
                      )}
                    </button>
                    
                    {/* Submenu */}
                    {organizationsExpanded && (
                      <ul className="mt-2 ml-8 space-y-1">
                        {item.submenu.map((subItem) => {
                          const isSubActive = window.location.pathname === subItem.href;
                          return (
                            <li key={subItem.name}>
                              <button
                                onClick={() => {
                                  navigate(subItem.href);
                                  setSidebarOpen(false); // Close sidebar on mobile after navigation
                                }}
                                className={`w-full flex items-center px-3 py-2 text-sm rounded-lg transition-colors ${
                                  isSubActive
                                    ? 'bg-whatsapp-500 text-white'
                                    : 'text-gray-400 hover:bg-gray-800 hover:text-white'
                                }`}
                              >
                                <subItem.icon className="h-4 w-4 mr-2" />
                                {subItem.name}
                              </button>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </li>
                );
              } else {
                const isActive = window.location.pathname === item.href;
                return (
                  <li key={item.name}>
                    <button
                      onClick={() => {
                        navigate(item.href);
                        setSidebarOpen(false); // Close sidebar on mobile after navigation
                      }}
                      className={`w-full flex items-center px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                        isActive
                          ? 'bg-whatsapp-600 text-white'
                          : 'text-gray-300 hover:bg-gray-800 hover:text-white'
                      }`}
                    >
                      <item.icon className="h-5 w-5 mr-3" />
                      {item.name}
                    </button>
                  </li>
                );
              }
            })}
          </ul>
        </nav>

        {/* Admin Info */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-gray-800">
          <div className="flex items-center">
            <div className="h-8 w-8 bg-whatsapp-600 rounded-full flex items-center justify-center">
              <span className="text-white text-sm font-bold">
                {admin?.fullName?.charAt(0) || 'A'}
              </span>
            </div>
            <div className="ml-3 flex-1">
              <p className="text-sm font-medium text-white truncate">{admin?.fullName}</p>
              <p className="text-xs text-gray-400 capitalize">{admin?.role}</p>
            </div>
            <button
              onClick={handleLogout}
              className="text-gray-400 hover:text-white"
              title="Logout"
            >
              <ArrowRightOnRectangleIcon className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="lg:pl-64">
        {/* Mobile header */}
        <div className="lg:hidden bg-white shadow-sm border-b border-gray-200 px-4 py-3">
          <div className="flex items-center justify-between">
            <button
              onClick={() => setSidebarOpen(true)}
              className="text-gray-600 hover:text-gray-900"
            >
              <Bars3Icon className="h-6 w-6" />
            </button>
            <h1 className="text-lg font-semibold text-gray-900">Admin Portal</h1>
            <div className="h-8 w-8 bg-whatsapp-600 rounded-full flex items-center justify-center">
              <span className="text-white text-sm font-bold">
                {admin?.fullName?.charAt(0) || 'A'}
              </span>
            </div>
          </div>
        </div>
        
        {children}
      </div>
    </div>
  );
};

export default AdminLayout;
